import { Component } from '@theme/component';

const ERROR_DISPLAY_DURATION = 6000;

/**
 * Standalone "Buy Now" button. Reads the variant id and quantity from the
 * enclosing product form at click time (never hardcoded), adds that line to
 * the cart via /cart/add.js, then redirects straight to /checkout. Does not
 * touch the cart drawer, the dynamic checkout button, or the normal
 * add-to-cart submit flow.
 */
class BuyNowComponent extends Component {
  requiredRefs = ['buyNowButton'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;

  /** @param {Event} event */
  async handleClick(event) {
    event.preventDefault();

    const button = this.refs.buyNowButton;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const form = button.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    const variantIdField = form.elements.namedItem('id');
    const quantityField = form.elements.namedItem('quantity');

    const variantId = variantIdField instanceof HTMLInputElement ? variantIdField.value : '';
    const quantity =
      quantityField instanceof HTMLInputElement && Number(quantityField.value) > 0
        ? Number(quantityField.value)
        : 1;

    if (!variantId) {
      this.#showError('Please select a variant before continuing.');
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'This item could not be added to your cart.');
      }

      window.location.href = '/checkout';
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      this.#showError(error instanceof Error ? error.message : 'This item could not be added to your cart.');
    }
  }

  /** @param {string} message */
  #showError(message) {
    const errorEl = this.refs.buyNowError;
    if (!(errorEl instanceof HTMLElement)) return;

    errorEl.textContent = message;
    errorEl.classList.remove('hidden');

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      errorEl.classList.add('hidden');
    }, ERROR_DISPLAY_DURATION);
  }
}

if (!customElements.get('buy-now-component')) {
  customElements.define('buy-now-component', BuyNowComponent);
}
